/**
 * Anchor-id generation and parsing helpers.
 *
 */
import RandomUtils from "@utils/random.utils.ts";
import { isEmpty } from "@utils/var.check";

// Generators

// For Question body anchor: <base> is in the format of `QN_<questionId>`
/** Question body anchor: `<base>_QB`. */
export function buildQuestionBodyAnchor(baseAnchor: string): string {
  return `${baseAnchor}_QB`;
}

/** Question stimulus anchor: `<base>_ST`. */
export function buildStimulusAnchor(baseAnchor: string): string {
  return `${baseAnchor}_ST`;
}

/** Student answer anchor: `<base>_SA`. */
export function buildStudentAnswerAnchor(baseAnchor: string): string {
  return `${baseAnchor}_SA`;
}

/** Suggested answer anchor: `<base>_SGA`. */
export function buildSuggestedAnswerAnchor(baseAnchor: string): string {
  return `${baseAnchor}_SGA`;
}

/** MCQ option body anchor: `<base>_MCQOPTION_<optionId>`. */
export function buildMcqOptionBodyAnchor(baseAnchor: string, optionId: string | number): string {
  return `${baseAnchor}_MCQOPTION_${optionId}`;
}

/** Speech-evaluation student answer anchor: `<base>_SA_SE`. */
export function buildSpeechEvaluationAnswerAnchor(baseAnchor: string): string {
  return `${baseAnchor}_SA_SE`;
}

/**
 * Teacher feedback group anchor: `<base>_TF`.
 * The per-comment teacher feedback anchor adds the comment id; see
 * {@link buildTeacherFeedbackCommentAnchor}.
 */
export function buildTeacherFeedbackAnchor(baseAnchor: string): string {
  return `${baseAnchor}_TF`;
}

/** Teacher feedback comment anchor: `<base>_TF_<commentId>`. */
export function buildTeacherFeedbackCommentAnchor(
  baseAnchor: string,
  commentId: string | number,
): string {
  return `${baseAnchor}_TF_${commentId}`;
}

/** Rubric criterion feedback anchor: `<base>_CF_<criteriaId>`. */
export function buildRubricCriterionFeedbackAnchor(
  baseAnchor: string,
  criteriaId: string | number,
): string {
  return `${baseAnchor}_CF_${criteriaId}`;
}

/** Base question anchor: `QN_<questionId>`. */
export function buildQuestionAnchor(questionId: string | number): string {
  return `QN_${questionId}`;
}

/** Quiz activity base anchor: `QZ_<quizActivityId>`. */
export function buildQuizAnchor(quizActivityId: string | number): string {
  return `QZ_${quizActivityId}`;
}

/** Quiz question anchor: `<quizAnchor>_QN_<questionId>`. */
export function buildQuizQuestionAnchor(quizAnchor: string, questionId: string | number): string {
  return `${quizAnchor}_QN_${questionId}`;
}

/** Multiple-part sub-question anchor: `<parentAnchor>_QN_<questionId>`. */
export function buildMultiplePartQuestionAnchor(
  parentAnchor: string,
  questionId: string | number,
): string {
  return `${parentAnchor}_QN_${questionId}`;
}

/**
 * Rich text anchor: `RT_<componentId>`, or `<parentAnchor>_RT_<componentId>`
 * when a parent anchor is present.
 */
export function buildRichTextAnchor(componentId: string | number, parentAnchor?: string): string {
  return isEmpty(parentAnchor) ? `RT_${componentId}` : `${parentAnchor}_RT_${componentId}`;
}

/**
 * Media object anchor: `<base>_<index>`, where `index` is the media object's
 * index token within rich text (e.g. `MO_0`).
 */
export function buildMediaObjectAnchor(baseAnchor: string, index: string | number): string {
  return `${baseAnchor}_${index}`;
}

/** Random per-question anchor: `<base>_<uuid>`. */
export function buildRandomQuestionAnchor(baseAnchor: string): string {
  return `${baseAnchor}_${RandomUtils.randomUuid()}`;
}

/** Poll body anchor: `PL_<componentId>_PB`. */
export function buildPollBodyAnchor(componentId: string | number): string {
  return `PL_${componentId}_PB`;
}

/** Discussion topic body anchor: `DN_<componentId>_DB`. */
export function buildDiscussionTopicBodyAnchor(componentId: string | number): string {
  return `DN_${componentId}_DB`;
}

// Parsing

/** Matches `QN_<n>` tokens in an anchor, e.g. for `QN_1_QN_2`. */
export function matchQuestionAnchorTokens(anchor: string): RegExpMatchArray | null {
  return anchor.match(/(QN)_\d+/g);
}

/** Matches `_RT_<n>` tokens in an anchor (rich text inside quiz). */
export function matchQuizRichTextTokens(anchor: string): RegExpMatchArray | null {
  return anchor.match(/_(RT)_\d+/g);
}

/** Whether the anchor refers to an answer (contains `SA`). */
export function isAnswerAnchor(anchor: string): boolean {
  return anchor.includes("SA");
}

/** Whether the anchor refers to rich text (contains `RT`). */
export function isRichTextAnchor(anchor: string): boolean {
  return anchor.includes("RT");
}

/** Whether the anchor is within a quiz activity (contains `QZ`). */
export function isQuizAnchor(anchor: string): boolean {
  return anchor.includes("QZ");
}

/** Whether the anchor is rooted at a quiz activity (`QZ_...`). */
export function isAnchorInQuiz(anchor: string): boolean {
  return anchor.startsWith("QZ");
}

/** Extracts the rich-text component id from a `RT_<id>` anchor, or null. */
export function parseRichTextComponentId(anchor: string): string | null {
  const match = anchor.match(/RT_(\d+)/);
  return match ? match[1] : null;
}

/** Extracts all `QN_<id>` question ids captured from an anchor. */
export function parseQuestionIds(anchor: string): string[] {
  return [...anchor.matchAll(/QN_(\d+)/g)].map((m) => m[1]);
}

/**
 * Resolves the numeric componentId encoded in an anchor.
 *  1. last `QN_<n>` token (offset 3 past `QN_`)
 *  2. else last `_RT_<n>` token (offset 4 past `_RT_`)
 *  3. else the second `_`-delimited segment.
 *
 * Note: for a nested anchor (e.g. an MPQ sub-question, `QN_<componentId>_QN_<childQuestionId>_QB`)
 * this returns the LAST token, i.e. the innermost question id, not the owning LessonActivityComponent
 * id. That's correct for this function's existing callers (grouping/sort-ordering, which want the
 * most specific id) - but wrong if you need the id of the actual component row to look up/validate.
 * Use parseOwningComponentIdFromAnchor for that instead.
 */
export function parseComponentIdFromAnchor(anchor: string): number {
  const questionMatches = matchQuestionAnchorTokens(anchor);
  const quizRichTextMatches = matchQuizRichTextTokens(anchor);
  if (questionMatches) {
    return parseInt(questionMatches[questionMatches.length - 1].substring(3));
  } else if (quizRichTextMatches) {
    return parseInt(quizRichTextMatches[quizRichTextMatches.length - 1].substring(4));
  }
  return parseInt(anchor.split("_")[1]);
}

/**
 * Resolves the id of the LessonActivityComponent that owns an anchor, regardless of nesting.
 *
 * Anchors are built root-first (buildMultiplePartQuestionAnchor/buildRichTextAnchor append nested
 * `_QN_<childId>`/`_RT_<id>` tokens onto an existing parentAnchor), so the FIRST `QN_<n>` token is
 * always the real component id - unlike parseComponentIdFromAnchor, which deliberately returns the
 * LAST (innermost) token for grouping purposes. Anywhere an id needs to resolve to an actual
 * LessonActivityComponent row (e.g. a backend existence check), use this instead of
 * parseComponentIdFromAnchor, or a nested anchor (MPQ sub-question, etc.) will resolve to a
 * question id instead of a component id.
 */
export function parseOwningComponentIdFromAnchor(anchor: string): number {
  const questionIds = parseQuestionIds(anchor);
  if (questionIds.length > 0) {
    return parseInt(questionIds[0]);
  }

  const quizRichTextMatches = matchQuizRichTextTokens(anchor);
  if (quizRichTextMatches) {
    return parseInt(quizRichTextMatches[quizRichTextMatches.length - 1].substring(4));
  }

  return parseInt(anchor.split("_")[1]);
}

/**
 * Resolves the ordered list of question ids encoded in an anchor (used by
 * AnnotationList grouping). The final element is the effective question id.
 */
export function parseQuestionIdsFromAnchor(anchor: string): string[] {
  const questionMatches = anchor.match(/QN_(\d+)(?:_QN_(\d+))*/g);
  const quizRichTextMatches = matchQuizRichTextTokens(anchor);
  const questionIds: string[] = [];

  if (questionMatches) {
    questionMatches.forEach((match: string) => {
      const ids = match.match(/QN_(\d+)/g)?.map((q: string) => q.split("_")[1]) || [];
      questionIds.push(...ids);
    });
  } else if (quizRichTextMatches) {
    questionIds.push(quizRichTextMatches[quizRichTextMatches.length - 1].substring(4));
  } else {
    questionIds.push(anchor.split("_")[1]);
  }

  return questionIds;
}
